import {
    ColumnDef,
    getCoreRowModel,
    Header,
    useReactTable,
} from '@tanstack/react-table'
import { ComponentProps, useCallback, useMemo, useRef } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'

import { usePaginationConfig } from '@/hooks/usePaginationConfig/usePaginationConfig'
import { ErrorState, SortingOrder, Table } from '@/components'

import { useEnergySourcingContractsTableLabelsMap } from './useEnergySourcingContractsTableLabelsMap'
import { getColumns } from './getColumns'
import { TableLoadingOverlay } from '@/components/common/Table/TableLoadingOverlay'
import { useDataManagementApi } from '@/hooks/useApi/useDataManagementApi/useDataManagementApi'
import { ContentPlaceholder } from '@/components/molecules'
import { EnergySourceAndContract } from '@/hooks/useApi/types'
import useAuthStore from '@/stores/useAuthStore/useAuthStore'

export const EnergySourcingContractsTableContainer: React.FC = () => {
    const labelsMap = useEnergySourcingContractsTableLabelsMap()
    const navigate = useNavigate()
    const user = useAuthStore((state) => state.user)
    const { sortBy, order, page, recordsPerPage } = useSearch({
        from: '/_layout/data-management/energy-sourcing-and-contracts',
    })
    const ref = useRef<HTMLDivElement>(null)

    const currentPage = page ?? 1
    const currentRecordsPerPage = recordsPerPage ?? 10
    const currentSortBy = sortBy ?? 'lastUpdate'
    const currentOrder = order ?? 'desc'

    const currentUserContactId = user?.contactId ?? null

    const onTableColumnHeaderClick = useCallback(
        (header: Header<unknown, unknown>) => {
            const columnId = header.id
            const newOrder =
                currentSortBy !== columnId || currentOrder === 'asc'
                    ? 'desc'
                    : 'asc'
            const newSortBy = columnId

            void navigate({
                to: '/data-management/energy-sourcing-and-contracts',
                from: '/data-management/energy-sourcing-and-contracts',
                search: (prev) => {
                    const next = {
                        ...prev,
                        sortBy: newSortBy,
                        order: newOrder,
                    }

                    return next
                },
            })
        },
        [navigate, currentOrder, currentSortBy]
    )

    const onPaginationInfoChange = useCallback(
        ({
            page,
            recordsPerPage,
        }: {
            page: number
            recordsPerPage: number
        }) => {
            void navigate({
                to: '/data-management/energy-sourcing-and-contracts',
                from: '/data-management/energy-sourcing-and-contracts',
                search: (prev) => {
                    const next = {
                        ...prev,
                        page: page,
                        recordsPerPage: recordsPerPage,
                    }

                    return next
                },
            })
        },
        [navigate]
    )

    const getColumnSorting = useCallback(
        (header: Header<unknown, unknown>) => {
            if (currentSortBy === header.id) {
                return currentOrder as SortingOrder
            }
            return undefined
        },
        [currentOrder, currentSortBy]
    )

    const { useEnergyContractsQuery } = useDataManagementApi()
    const { data, isSuccess, isLoading, isFetching, isError } =
        useEnergyContractsQuery({
            sortBy: currentSortBy,
            order: currentOrder,
            offset: (currentPage - 1) * currentRecordsPerPage,
            limit: currentRecordsPerPage,
        })

    const columns = useMemo(
        () => getColumns(labelsMap, currentUserContactId),
        [labelsMap, currentUserContactId]
    )

    const pagination = usePaginationConfig({
        totalItems: data?.totalItems || 0,
        onPageChange: (newPage) => {
            onPaginationInfoChange({
                page: newPage,
                recordsPerPage: currentRecordsPerPage,
            })
        },
        onRecordsPerPageChange: (newRecordsPerPage) => {
            onPaginationInfoChange({
                page: 1,
                recordsPerPage: Number(newRecordsPerPage),
            })
        },
        siblingCount: 2,
        currentPage: currentPage,
        recordsPerPage: currentRecordsPerPage,
    })

    const emptyData = Array.from(new Array(50)).map(() => {
        const emptyContract = {}

        return emptyContract
    })

    const isLoadingState = isLoading || isFetching

    if (!isLoadingState) {
        if (isSuccess && data.items.length === 0) {
            return <EnergySourcingContractsEmptyState />
        }

        if (isError) {
            return <EnergySourcingContractsErrorState />
        }
    }

    return (
        <EnergySourcingContractsTable
            columns={columns}
            onTableColumnHeaderClick={onTableColumnHeaderClick}
            getColumnSorting={getColumnSorting}
            data={data?.items ?? emptyData}
            isLoading={isLoadingState}
            pagination={pagination}
            tableWrapperRef={ref}
        />
    )
}

const EnergySourcingContractsErrorState = () => {
    const { t } = useTranslation('translations')

    return (
        <ErrorState
            description={t(
                'energy_sourcing_contracts_table.error_state.description'
            )}
            className="h-full"
        />
    )
}

const EnergySourcingContractsEmptyState = () => {
    const { t } = useTranslation('translations')
    const title = t('energy_sourcing_contracts_table.empty_state.title')
    const description = t(
        'energy_sourcing_contracts_table.empty_state.description'
    )

    return (
        <ContentPlaceholder
            className="h-full"
            illustrationSrc="/assets/no-reports-illustration.svg"
            title={title}
            description={description}
            name="empty-state"
        />
    )
}

type EnergySourcingContractsTableProps = {
    data: EnergySourceAndContract[] | Record<never, unknown>[]
    columns: ColumnDef<EnergySourceAndContract>[]
    onTableColumnHeaderClick: ComponentProps<
        typeof Table
    >['onTableColumnHeaderClick']
    getColumnSorting: ComponentProps<typeof Table>['getColumnSorting']
    isLoading: boolean
    pagination: ComponentProps<typeof Table>['pagination']
    tableWrapperRef?: React.RefObject<HTMLDivElement>
}

export const EnergySourcingContractsTable: React.FC<
    EnergySourcingContractsTableProps
> = (props) => {
    const {
        data,
        columns,
        onTableColumnHeaderClick,
        getColumnSorting,
        isLoading,
        pagination,
        tableWrapperRef,
    } = props

    const table = useReactTable({
        data: data,
        columns: columns as ColumnDef<unknown>[],
        getCoreRowModel: getCoreRowModel(),
    })

    const { t } = useTranslation('translations')
    const loadingTitle = t('common.loading')

    return (
        <div className="relative h-full">
            <Table
                table={table}
                onTableColumnHeaderClick={onTableColumnHeaderClick}
                getColumnSorting={getColumnSorting}
                pagination={pagination}
                style="list"
                tableWrapperRef={tableWrapperRef}
                cellClassName="first:max-w-0"
            />

            <TableLoadingOverlay show={isLoading} title={loadingTitle} />
        </div>
    )
}
